import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faUpload,
  faUserTie,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { getToken } from "../../function/token";
import domain from "../../../reusecomponent/domain";
import {
  ErrorToastContainer,
  SuccessToastContainer,
} from "../../function/CustomFunction";
import "./agents.css";

function AddAgentModal({ showModal, onClose, onAgentSaved, editingAgent }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile_number: "",
    password: "",
    role_id: "",
    role_name: "",
    is_active: 1,
    profilePicture: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Fetch roles when component mounts
  useEffect(() => {
    if (showModal) {
      fetchRoles();
    }
  }, [showModal]);

  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const token = getToken();
      const response = await fetch(`${domain()}/agen_role`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result.status) {
        // Handle different possible response structures
        let rolesData = [];
        if (Array.isArray(result.data)) {
          rolesData = result.data;
        } else if (result.data && Array.isArray(result.data.data)) {
          rolesData = result.data.data;
        } else if (result.data && Array.isArray(result.data.roles)) {
          rolesData = result.data.roles;
        } else if (Array.isArray(result.roles)) {
          rolesData = result.roles;
        } else {
          rolesData = [];
        }

        setRoles(rolesData);
      } else {
        console.error("Failed to fetch roles:", result.message);
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    if (editingAgent) {
      console.log("Setting form data with editingAgent:", editingAgent);

      setFormData({
        name: editingAgent.name || "",
        email: editingAgent.email || "",
        mobile_number: editingAgent.mobile_number || "",
        password: "", // Always empty for security
        role_id: editingAgent.user_role || editingAgent.role_id || "",
        role_name: editingAgent.role_name || "",
        is_active: editingAgent.isactive || 1,
        profilePicture: null,
      });

      console.log("Form data set:", {
        name: editingAgent.name || "",
        email: editingAgent.email || "",
        mobile_number: editingAgent.mobile_number || "",
        role_id: editingAgent.user_role || editingAgent.role_id || "",
        is_active: editingAgent.isactive || 1,
        role_name: editingAgent.role_name,
      });

      // Set preview image if profile picture exists
      if (editingAgent.profile_picture) {
        setPreviewImage(
          `https://api.campaigningtool.com/agent/${editingAgent.profile_picture}`
        );
      } else {
        setPreviewImage(
          "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_1280.png"
        );
      }
    } else {
      setFormData({
        name: "",
        email: "",
        mobile_number: "",
        password: "",
        role_id: "",
        role_name: "",
        is_active: 1,
        profilePicture: null,
      });
      setPreviewImage(null);
    }
    setErrors({});
  }, [editingAgent, showModal]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.mobile_number.trim()) {
      newErrors.mobile_number = "number is required";
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.mobile_number)) {
      newErrors.mobile_number = "Please enter a valid phone number";
    }

    if (!editingAgent && !formData.password.trim()) {
      newErrors.password = "password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        ErrorToastContainer("Please select a valid image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        ErrorToastContainer("Image size should be less than 5MB");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        profilePicture: file,
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = getToken();
      let response, result;
      let profilePictureUrl = null;

      // Create/update agent with image file if provided
      const body = {
        name: formData.name,
        isactive: formData.is_active,
        email: formData.email,
        mobile_number: formData.mobile_number,
      };

      if (formData.password.trim()) {
        body.password = formData.password;
      }
      if (formData.role_id) {
        body.role_id = formData.role_id;
      }

      // If image is provided, append it to the body
      if (formData.profilePicture) {
        body.profile_picture = formData.profilePicture;
      }

      // Send request with FormData if image exists, otherwise JSON
      if (formData.profilePicture) {
        const formDataToSend = new FormData();
        Object.keys(body).forEach((key) => {
          formDataToSend.append(key, body[key]);
        });

        response = await fetch(
          `${domain()}/agents${
            editingAgent ? `/${btoa(editingAgent.id)}` : ""
          }`,
          {
            method: editingAgent ? "POST" : "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formDataToSend,
          }
        );
      } else {
        response = await fetch(
          `${domain()}/agents${
            editingAgent ? `/${btoa(editingAgent.id)}` : ""
          }`,
          {
            method: editingAgent ? "POST" : "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          }
        );
      }

      result = await response.json();

      if (result.status) {
        SuccessToastContainer(
          editingAgent
            ? "Agent updated successfully!"
            : "Agent created successfully!"
        );
        onAgentSaved();
      } else {
        ErrorToastContainer(result.message || "Failed to save agent");
      }
    } catch (error) {
      console.error("Error saving agent:", error);
      ErrorToastContainer("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!showModal) return null;

  return (
    <div
      className="modal fade show agent-modal"
      style={{
        display: "block",
        position: "fixed",
        zIndex: 1050,
        left: 0,
        top: 0,
        width: "100vw",
        height: "100vh",
        overflow: "auto",
        background: "rgba(0,0,0,0.15)",
        pointerEvents: "auto",
      }}
      tabIndex="-1"
    >
      <div
        className="modal-dialog modal-lg"
        style={{
          zIndex: 1060,
          pointerEvents: "auto",
          margin: "2rem auto",
        }}
      >
        <div className="modal-content" style={{ pointerEvents: "auto" }}>
          <div className="modal-header">
            <h5 className="modal-title">
              <FontAwesomeIcon icon={faUserTie} className="me-2" />
              {editingAgent ? "Edit Agent" : "Add New Agent"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isSubmitting}
            ></button>
          </div>

          <form onSubmit={handleSubmit} className="agent-form">
            <div className="modal-body">
              <div className="row">
                {/* Profile Picture Upload */}
                <div className="col-12 mb-4">
                  <div className="text-center">
                    <div className="position-relative d-inline-block">
                      <div
                        className="rounded-circle border border-3 border-dashed d-flex align-items-center justify-content-center"
                        style={{
                          width: "120px",
                          height: "120px",
                          background: "#f8f9fa",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          document.getElementById("profilePicture").click()
                        }
                      >
                        {previewImage ? (
                          <img
                            src={
                              previewImage ||
                              "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_1280.png"
                            }
                            alt="Profile Preview"
                            className="rounded-circle"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div className="text-center">
                            <FontAwesomeIcon
                              icon={faUpload}
                              size="2x"
                              className="text-muted mb-2"
                            />
                            <div className="small text-muted">Upload Photo</div>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        id="profilePicture"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">
                        Click to upload profile picture (optional, max 5MB)
                      </small>
                    </div>
                  </div>
                </div>

                {/* Name Field */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="name" className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter agent name"
                    disabled={isSubmitting}
                    required
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                {/* Email Field */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="email" className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className={`form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter agent email"
                    disabled={isSubmitting}
                    required
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>

                {/* Conditionally render password field only when adding a new agent */}
                {!editingAgent && (
                  <div className="col-md-6 mb-3">
                    <label htmlFor="password" className="form-label">
                      Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className={`form-control ${
                        errors.password ? "is-invalid" : ""
                      }`}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter agent password"
                      disabled={isSubmitting}
                      required
                    />
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>
                )}

                {/* Role Selection */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="role_id" className="form-label">
                    Role
                  </label>
                  <select
                    className={`form-control agent-select ${
                      errors.role_id ? "is-invalid" : ""
                    }`}
                    id="role_id"
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleInputChange}
                    disabled={isSubmitting || loadingRoles}
                  >
                    <option value="">Select a role</option>

                    {/* Show the current agent's role immediately if editing */}
                    {editingAgent &&
                      editingAgent.role_name &&
                      editingAgent.user_role && (
                        <option
                          key={`current-${editingAgent.user_role}`}
                          value={editingAgent.user_role}
                        >
                          {editingAgent.role_name}
                        </option>
                      )}

                    {loadingRoles ? (
                      <option value="">Loading roles...</option>
                    ) : roles.length === 0 ? (
                      <option value="">No roles available</option>
                    ) : (
                      roles
                        .filter(
                          (role) =>
                            !editingAgent || role.id !== editingAgent.user_role
                        ) // Avoid duplicates
                        .map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.role_name || role.name}
                          </option>
                        ))
                    )}
                  </select>
                  {errors.role_id && (
                    <div className="invalid-feedback">{errors.role_id}</div>
                  )}
                </div>
                {/* Status Field */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="is_active" className="form-label">
                    Status
                  </label>
                  <select
                    className="form-control agent-select"
                    id="is_active"
                    name="is_active"
                    value={formData.is_active}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                {/* mobile_number Field */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="mobile_number" className="form-label">
                    Phone number<span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${
                      errors.mobile_number ? "is-invalid" : ""
                    }`}
                    id="number"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    disabled={isSubmitting}
                    required
                  />
                  {errors.mobile_number && (
                    <div className="invalid-feedback">
                      {errors.mobile_number}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    {isUploadingImage
                      ? "Uploading Image..."
                      : editingAgent
                      ? "Updating..."
                      : "Creating..."}
                  </>
                ) : editingAgent ? (
                  "Update Agent"
                ) : (
                  "Create Agent"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddAgentModal;
